import { useId } from 'react';
import type { SelectMultipleItem as SelectMultipleItemType } from '@webforge-survey/shared';
import { ItemShell } from './ItemShell';

interface SelectMultipleItemProps {
  item: SelectMultipleItemType;
  value: string[];
  error?: string;
  onChange: (value: string[]) => void;
}

/**
 * select_multiple : checkboxes groupées dans un `fieldset`.
 * Gère les options exclusives : lorsqu'une option exclusive est cochée, on
 * efface les autres (et inversement). Le store fait déjà la mutation au
 * niveau global ; ici, on prépare la prochaine valeur en cohérence.
 *
 * Message inline (aria-live=polite) lorsqu'une exclusive vient d'être
 * activée.
 */
export function SelectMultipleItem({
  item,
  value,
  error,
  onChange,
}: SelectMultipleItemProps) {
  const liveId = useId();
  const exclusives = item.exclusive ?? [];
  const exclusiveSelected = value.find((v) => exclusives.includes(v));

  const toggle = (choiceValue: string) => {
    const isAlready = value.includes(choiceValue);
    if (isAlready) {
      onChange(value.filter((v) => v !== choiceValue));
      return;
    }
    if (exclusives.includes(choiceValue)) {
      onChange([choiceValue]); // exclusive : remplace tout
      return;
    }
    // valeur normale : retire les exclusives, ajoute la valeur
    const filtered = value.filter((v) => !exclusives.includes(v));
    onChange([...filtered, choiceValue]);
  };

  return (
    <ItemShell
      name={item.name}
      label={item.label}
      {...(item.hint !== undefined && { hint: item.hint })}
      {...(item.required !== undefined && { required: item.required })}
      {...(error !== undefined && { error })}
      role="group"
    >
      <div className="space-y-2 max-w-prose">
        {item.choices.map((choice) => {
          const selected = value.includes(choice.value);
          const isExclusive = exclusives.includes(choice.value);
          const id = `${item.name}-${choice.value}`;
          return (
            <label
              key={choice.value}
              htmlFor={id}
              className={`option-card ${selected ? 'option-card-selected' : ''}`}
            >
              <input
                id={id}
                type="checkbox"
                name={item.name}
                value={choice.value}
                checked={selected}
                onChange={() => toggle(choice.value)}
                className="mt-1 h-5 w-5 accent-accent flex-shrink-0"
              />
              <span className="text-text leading-snug">
                {choice.label}
                {isExclusive && (
                  <span className="ml-2 text-xs text-text-muted">
                    (exclusif)
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
      {exclusiveSelected && (
        <p
          id={liveId}
          aria-live="polite"
          className="text-sm text-text-muted mt-2"
        >
          « {item.choices.find((c) => c.value === exclusiveSelected)?.label} »
          a été sélectionné — les autres options sont désactivées.
        </p>
      )}
    </ItemShell>
  );
}
